/**
 * Mobile-optimized collapsible parameter row for ICP test detail
 * Shows Status, Parameter, Value, RangeBar in collapsed state
 * Expands to show Unit, Range, Desired, Dosing, Importance
 */
import { useState } from 'react';
import {
  Box,
  Collapse,
  Group,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
  Divider,
  Anchor,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconCircleFilled,
  IconArrowUp,
  IconArrowDown,
} from '@tabler/icons-react';
import { RangeBar } from '@/components/RangeBar';
import type { ReferenceRange } from '@/types/sensor';

export const BELOW_DETECTION_TOOLTIP = 'Not detectable — below detection limit';

// Extracted StatusIcon component to prevent re-creation on every render
function StatusIcon({ detailedStatus }: { detailedStatus: 'in-range' | 'above-range' | 'below-range' | 'no-range' }) {
  switch (detailedStatus) {
    case 'in-range':
      return <IconCircleFilled size={12} color="var(--mantine-color-green-6)" />;
    case 'above-range':
      return <IconArrowUp size={14} color="var(--mantine-color-red-6)" stroke={3} />;
    case 'below-range':
      return <IconArrowDown size={14} color="var(--mantine-color-red-6)" stroke={3} />;
    default:
      return <IconCircleFilled size={12} color="var(--mantine-color-gray-4)" />;
  }
}

interface ParameterRowMobileProps {
  label: string;
  value: number | null;
  formattedValue: string;
  unit: string;
  range: ReferenceRange | undefined;
  status: 'in-range' | 'out-of-range' | 'no-range' | 'unknown';
  detailedStatus: 'in-range' | 'above-range' | 'below-range' | 'no-range';
  categoryColor: string;
  /** Provider reported the value as below its detection limit ("n.d.") */
  belowDetection?: boolean;
  /** Header for the range row: "Set Point Range" (default) or "Ideal Value" (ATI) */
  rangeLabel?: string;
  /** Hide the Desired row when the range column already shows the ideal value (ATI) */
  showDesired?: boolean;
  /** Importance is a Reef Zelements-only concept */
  showImportance?: boolean;
  onViewNarrative?: () => void;
}

export function ParameterRowMobile({
  label,
  value,
  formattedValue,
  unit,
  range,
  status,
  detailedStatus,
  categoryColor,
  belowDetection = false,
  rangeLabel = 'Set Point Range',
  showDesired = true,
  showImportance = true,
  onViewNarrative,
}: ParameterRowMobileProps) {
  const [expanded, setExpanded] = useState(false);

  // Cell shows whichever the provider supplied (set-point range or ideal value)
  const rangeDisplay = range?.set_point_range || range?.desired_level || '-';

  return (
    <Box
      style={{
        borderBottom: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <UnstyledButton
        onClick={() => setExpanded(!expanded)}
        style={{ width: '100%', padding: '12px 8px' }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <StatusIcon detailedStatus={detailedStatus} />
            <Text size="sm" fw={500} truncate>
              {label}
            </Text>
          </Group>

          <Group gap="sm" wrap="nowrap">
            {belowDetection ? (
              <Tooltip label={BELOW_DETECTION_TOOLTIP} events={{ hover: true, focus: true, touch: true }}>
                <Text size="sm" fw={700} c="dimmed">
                  n.d.
                </Text>
              </Tooltip>
            ) : (
              <Text
                size="sm"
                fw={700}
                c={status === 'out-of-range' ? 'red' : categoryColor}
              >
                {formattedValue}
              </Text>
            )}

            <Box style={{ width: 100 }}>
              {value !== null && range ? (
                <RangeBar
                  value={value}
                  rangeStr={range.set_point_range}
                  desiredStr={range.desired_level}
                  inRange={status === 'in-range'}
                  compact
                />
              ) : (
                <Text size="xs" c="dimmed">-</Text>
              )}
            </Box>

            {expanded ? (
              <IconChevronUp size={16} color="var(--mantine-color-gray-6)" />
            ) : (
              <IconChevronDown size={16} color="var(--mantine-color-gray-6)" />
            )}
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
        <Box px="md" pb="md" pt="xs" bg="gray.0">
          <Divider mb="sm" />
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="xs" c="dimmed">Unit</Text>
              <Text size="xs">{unit || '-'}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{rangeLabel}</Text>
              <Text size="xs">{rangeDisplay}</Text>
            </Group>
            {showDesired && (
              <Group justify="space-between">
                <Text size="xs" c="dimmed">Desired</Text>
                <Text size="xs">{range?.desired_level || '-'}</Text>
              </Group>
            )}
            <Group justify="space-between">
              <Text size="xs" c="dimmed">Dosing</Text>
              <Text size="xs">{range?.dosing_recommendation || '-'}</Text>
            </Group>
            {showImportance && (
              <Group justify="space-between">
                <Text size="xs" c="dimmed">Importance</Text>
                <Text size="xs">{range?.importance || '-'}</Text>
              </Group>
            )}
            {range?.narrative_text && onViewNarrative && (
              <Anchor size="xs" onClick={onViewNarrative}>
                View dosing info
              </Anchor>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
